/*
 * Configuration to control the variation possible from the components
 * that exists within the different layouts outlined.
 *
 * Good Ex: Whether the SEO side rail content appears on the page is logic that should be
 *  determined by this config.
 * Bad Ex: Which Jumbotron banner should be used for the config (this is determined by the layout
 *  used by the entity type, not this high level config)
 *
 */

/*
 * Function to get the EntityConfig obj from this file.  Some entities do not have data
 * for attributes such as performerType or geographyType so it is used in conjunction with
 * helpers such as isPartner and isTheatre to fetch the correct config per entity page.
 */
export default function getEntityConfigObj(entity, theme = {}) {
  let configObj;
  let performerType;
  let {entityType} = entity;
  const {isTheatre, isArtist, dynamicAttributes = []} = entity;

  /*
   * @Todo return the isCityPageValidAttribute from entity-helper
   *  Logic to check whether it is citypage
   */
  let isCityPageValidAttribute = false;
  const cityHeaderAttr = dynamicAttributes.find((item) => item.name === 'header_copy');
  if (cityHeaderAttr) {
    isCityPageValidAttribute = true;
  }
  entityType = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  let isPartner = false;
  let isUniquePartner = false;

  if (theme) {
    ({isPartner, isUniquePartner} = theme);
  }

  switch (entityType) {
    case 'Performer':
      ({performerType} = entity);
      if (!performerType && isArtist) {
        performerType = 'Artist'; // TBD: Endorse this Hack
      }
      configObj = EntityConfig[performerType];
      if (performerType === 'Team' && isPartner) {
        configObj = EntityConfig.SportsPartner;
      } else if (isTheatre) {
        configObj = EntityConfig.Theatre;
      }
      if (isUniquePartner) {
        configObj = EntityConfig.UniquePartner;
      }
      // @TODO: Fix to ensure performers without entity.performerType don't crash page
      if (!configObj) {
        configObj = EntityConfig.Default;
      }
      break;
    case 'Grouping':
      configObj = isPartner ? EntityConfig.SportsPartnerGrouping : EntityConfig[entityType];
      break;
    case 'Geography':
      configObj = EntityConfig[entityType];
      // Use dynamic attribute to check whether it is citypage since some city have state as geographyType
      if (isCityPageValidAttribute) {
        configObj = EntityConfig.City;
      }
      break;
    case 'Category':
    case 'Venue':
      configObj = EntityConfig[entityType];
      break;
    default:
      configObj = EntityConfig[entityType] || EntityConfig.Default;
      break;
  }

  return configObj;
}

/*
 * If a component / config option does not have a value, it is presumed to be false and will not display
 *
 * List of possible options: --> Please contribute to this list as we add new options
 * - EntityLayout (string): Determines which entity page layout is used to display page content
 * - PillsConfig (string): Determines which type of pill configuration to display
 * - ChildSEOLinks (bool): Component that displays related entity child links for SEO
 * - HidePanelHeader (bool): Flag to indicate whether the event count panel header should display in event list
 */
export const EntityConfig = {
  Artist: {
    ConfigType: 'Artist',
    EntityLayout: 'Default',
    PillsConfig: 'Default',
  },
  Theatre: {
    ConfigType: 'Theatre',
    EntityLayout: 'Default',
    PillsConfig: 'Default',
  },
  Team: {
    ConfigType: 'Team',
    EntityLayout: 'Default',
    PillsConfig: 'Sports',
  },
  SportsPartner: {
    ConfigType: 'SportsPartner',
    EntityLayout: 'Default',
    PillsConfig: 'SportsPartner',
  },
  SportsPartnerGrouping: {
    ConfigType: 'SportsPartnerGrouping',
    EntityLayout: 'Default',
    PillsConfig: 'SportsPartnerGrouping',
  },
  UniquePartner: {
    ConfigType: 'UniquePartner',
    EntityLayout: 'Default',
    PillsConfig: 'UniquePartner',
  },
  Venue: {
    ConfigType: 'Venue',
    EntityLayout: 'Default',
    PillsConfig: 'Default',
    HidePanelHeader: true,
  },
  Grouping: {
    ConfigType: 'Grouping',
    EntityLayout: 'Default',
    PillsConfig: 'CategoryGrouping',
  },
  Category: {
    ConfigType: 'Category',
    EntityLayout: 'Default',
    PillsConfig: 'CategoryGrouping',
  },
  Geography: {
    ConfigType: 'Geography',
    EntityLayout: 'Content',
    PillsConfig: 'Geography',
    ChildSEOLinks: true,
  },
  City: {
    ConfigType: 'City',
    EntityLayout: 'Content',
    PillsConfig: 'City',
  },
  Default: {
    ConfigType: 'Default',
    EntityLayout: 'Default',
    PillsConfig: 'Default',
  },
};
